import type { Core } from "@pdftron/webviewer"

// The source PDF carries native Link annotations (sheet cross-references). Apryse runs a link's GoTo
// on a click, so clicking a highlight that overlaps one jumped the pane to another page. Nothing here
// uses them, so they are dropped from the manager; they are imported, so the file is untouched.
type TAnnotationManager = Core.AnnotationManager
type TLinkClass = typeof Core.Annotations.Link

const removeLinks = (manager: TAnnotationManager, Link: TLinkClass, annotations: Core.Annotations.Annotation[]) => {
	const links = annotations.filter((annotation) => annotation instanceof Link)
	if (links.length > 0) manager.deleteAnnotations(links, { imported: true, force: true })
}

/**
 * Removes the PDF's link annotations now and whenever more are imported, in every viewer mode.
 * Returns the unbind; a viewer without an annotation manager yet binds nothing.
 */
export const bindPdfLinkRemoval = (documentViewer: Core.DocumentViewer | undefined, Link: TLinkClass): (() => void) => {
	const manager = documentViewer?.getAnnotationManager?.()
	if (!documentViewer || !manager) return () => {}
	const onChanged = (annotations: Core.Annotations.Annotation[], action: string, info?: { imported?: boolean }) => {
		if (action === "add" && info?.imported) removeLinks(manager, Link, annotations)
	}
	const onLoaded = () => removeLinks(manager, Link, manager.getAnnotationsList())

	onLoaded()
	manager.addEventListener("annotationChanged", onChanged)
	documentViewer.addEventListener("annotationsLoaded", onLoaded)
	return () => {
		// The viewer may already be disposed (split exit); a throw here would skip the other viewers' unbinds.
		try {
			manager.removeEventListener("annotationChanged", onChanged)
			documentViewer.removeEventListener("annotationsLoaded", onLoaded)
		} catch {
			// nothing left to unbind
		}
	}
}
